import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { normalizeTeamSiteUrl } from '../utils/sharepointSiteUrl.js';
import {
  connectSite,
  isSharepointSitesApiAvailable,
  listConnectedSites,
  previewSite,
  getAdminConsentUrl, 
} from '../api/sharepointSitesApi.js';
import { phSharePointConnected, phSharePointConnectFailed } from '../utils/posthogEvents.js';

const SECURITY_TEXT =
  'CODiii only gets access to the one site we detect from your link — not all of SharePoint.\n\n'
  + 'Only sign in when the official Microsoft login page opens (login.microsoftonline.com). '
  + 'CODiii never sees your password.';

function formatLogTime() {
  return new Date().toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function normRole(v) {
  return String(v || '').trim().toUpperCase();
}

const ROLE_LABELS = {
  ADMIN: 'Admin',
  BILLING_ADMIN: 'Billing Admin',
  MEMBER: 'Member',
};

function roleLabel(role) {
  const key = normRole(role);
  return ROLE_LABELS[key] || key || '—';
}

export default function SharePointConnectPage() {
  const { user } = useAuth();
  const membership = user?.companies?.[0] || null;
  const companyId = membership?.companyId || '';
  const myRole = normRole(membership?.role);
  const canConnect = myRole === 'ADMIN' || myRole === 'BILLING_ADMIN';

  const [urlInput, setUrlInput] = useState('');
  const [clientPreview, setClientPreview] = useState(null);
  const [clientPreviewError, setClientPreviewError] = useState('');
  const [serverPreview, setServerPreview] = useState(null);
  const [connectedSites, setConnectedSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [logLines, setLogLines] = useState([]);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const apiConfigured = isSharepointSitesApiAvailable();

  const appendLog = useCallback((message) => {
    setLogLines((prev) => [...prev.slice(-80), `${formatLogTime()}  ${message}`]);
  }, []);

  const refreshSites = useCallback(async () => {
    if (!companyId || !canConnect || !apiConfigured) {
      setConnectedSites([]);
      setSitesLoading(false);
      return;
    }
    setSitesLoading(true);
    try {
      const sites = await listConnectedSites(companyId);
      setConnectedSites(Array.isArray(sites) ? sites : []);
    } catch (e) {
      if (e.status === 404 || e.status === 501) {
        appendLog('Connected sites list is not available yet (backend pending).');
      } else {
        appendLog(`Could not load connected sites: ${e.message}`);
      }
      setConnectedSites([]);
    } finally {
      setSitesLoading(false);
    }
  }, [apiConfigured, appendLog, canConnect, companyId]);

  useEffect(() => {
    refreshSites();
  }, [refreshSites]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connectSuccess') === '1') {
      const name = params.get('siteName') || 'Site';
      appendLog(`Success: ${name} is connected.`);
      setStatus({
        type: 'ok',
        text: `${name} is now connected. Your team can paste folder links from this site in CODiii.`,
      });
      window.history.replaceState({}, '', window.location.pathname);
      refreshSites();
      return;
    }
    if (params.get('msAuth') === 'success') {
      appendLog('Microsoft sign-in complete. Click Connect site to finish.');
      setStatus({
        type: 'ok',
        text: 'Microsoft sign-in complete. Click Connect site to CODiii to finish.',
      });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (params.get('msAuth') === 'error') {
      const msg = params.get('message') || 'Microsoft sign-in failed.';
      appendLog(`Microsoft sign-in failed: ${msg}`);
      setStatus({ type: 'err', text: msg });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [appendLog, refreshSites]);

  useEffect(() => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setClientPreview(null);
      setClientPreviewError('');
      setServerPreview(null);
      return;
    }
    try {
      setClientPreview(normalizeTeamSiteUrl(trimmed));
      setClientPreviewError('');
    } catch (e) {
      setClientPreview(null);
      setClientPreviewError(e.message || 'Invalid link.');
      setServerPreview(null);
    }
  }, [urlInput]);

  useEffect(() => {
    if (!clientPreview || !companyId || !apiConfigured) {
      setServerPreview(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await previewSite(companyId, urlInput.trim());
        if (!cancelled) setServerPreview(data);
      } catch {
        if (!cancelled) setServerPreview(null);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [apiConfigured, clientPreview, companyId, urlInput]);

  const previewLabel = useMemo(() => {
    if (clientPreviewError) return null;
    if (!clientPreview) {
      return 'After you paste a URL, we will show the site we will connect.';
    }
    const name = serverPreview?.siteName;
    return name ? `Site to connect: ${name}` : 'Site to connect:';
  }, [clientPreview, clientPreviewError, serverPreview?.siteName]);

  async function handleConnect() {
    setStatus({ type: '', text: '' });
    try {
      normalizeTeamSiteUrl(urlInput);
    } catch (e) {
      setStatus({ type: 'err', text: e.message });
      return;
    }
    if (!companyId || !canConnect) {
      setStatus({ type: 'err', text: 'You do not have permission to connect SharePoint sites.' });
      return;
    }
    if (!apiConfigured) {
      setStatus({ type: 'err', text: 'Connect API is not configured on this environment.' });
      return;
    }

    setConnecting(true);
    appendLog(`Connecting site: ${clientPreview}`);
    try {
      const result = await connectSite(companyId, urlInput.trim());
      const name = result?.siteName || clientPreview;
      appendLog(`Success: ${name} is connected.`);
      setStatus({
        type: 'ok',
        text: `${name} is now connected. Your team can paste folder links from this site in CODiii.`,
      });
      phSharePointConnected({ siteName: name });
      setUrlInput('');
      await refreshSites();
    } catch (e) {
      if (e.code === 'MS_ADMIN_CONSENT_REQUIRED' && e.data?.connectUrl) {
        appendLog('Microsoft admin sign-in required — opening login window.');
        window.location.assign(e.data.connectUrl);
        return;
      }
      if (e.status === 501 || e.status === 404) {
        appendLog('Connect API is not fully deployed yet.');
        setStatus({
          type: 'err',
          text: e.message || 'The connect service is not available yet. Backend setup is in progress.',
        });
        phSharePointConnectFailed({ reason: 'not_deployed' });
      } else {
        appendLog(`Connect failed: ${e.message}`);
        setStatus({ type: 'err', text: e.message });
        phSharePointConnectFailed({ reason: e.code || e.message });
      }
    } finally {
      setConnecting(false);
    }
  }
  async function handleGrantTenantConsent() {
    setStatus({ type: '', text: '' });
    try {
      const consentUrl = await getAdminConsentUrl();
      if (!consentUrl) {
        setStatus({ type: 'err', text: 'Could not get the consent link. Try again.' });
        return;
      }
      window.location.assign(consentUrl);
    } catch (e) {
      setStatus({ type: 'err', text: e.message || 'Could not start admin consent.' });
    }
  }

  if (!membership) {
    return (
      <div className="page">
        <div className="card">
          <h1 className="logo">CODiii</h1>
          <p className="subtitle">No company access found for your account.</p>
          <Link to="/login" className="btn btn-outline">Sign in</Link>
        </div>
      </div>
    );
  }

  if (!canConnect) {
    return (
      <div className="page">
        <div className="card">
          <h1 className="logo">Connect SharePoint</h1>
          <p className="subtitle">
            Only company administrators can connect SharePoint sites. Your role: {roleLabel(myRole)}.
          </p>
          <Link to="/" className="btn btn-outline">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page sp-connect-page" style={{ alignItems: 'flex-start', paddingTop: 32 }}>
      <div className="card card-wide">
        <div className="toolbar">
          <div>
            <h1 className="logo" style={{ marginBottom: 4, fontSize: 22 }}>
              Connect SharePoint to CODiii
            </h1>
            <p className="subtitle" style={{ margin: 0 }}>
              IT setup — once per team site. No product license required.
            </p>
          </div>
          <Link to="/" className="btn btn-outline">
            Back to dashboard
          </Link>
        </div>

        <section className="section sp-connect-intro">
          <ul className="sp-intro-list">
            <li>Run once per site (not per person and not per folder).</li>
            <li>You need Global Administrator or SharePoint Administrator rights.</li>
            <li>
              Sign-in opens in your browser on login.microsoftonline.com.
              {' '}
              CODiii never sees your password.
            </li>
          </ul>
          <button
            type="button"
            className="link-btn"
            onClick={() => setSecurityOpen((v) => !v)}
          >
            Security details
          </button>
          {securityOpen && (
            <div className="sp-help-panel" style={{ whiteSpace: 'pre-line' }}>
              {SECURITY_TEXT}
            </div>
          )}
        </section>

        <section className="section">
          <h2>Step 1: Authorize CODiii for your organization</h2>
          <p className="subtitle" style={{ marginTop: 0 }}>
            One-time setup by a Global Administrator. This lets CODiii access the specific
            SharePoint sites you connect below — nothing else.
          </p>
          <div className="form-actions">
            <button type="button" className="btn" onClick={handleGrantTenantConsent}>
              Authorize organization
            </button>
          </div>
        </section>
        
        <section className="section">
          <h2>SharePoint link</h2>
          <p className="subtitle" style={{ marginTop: 0 }}>
            What to paste: any link from the team site — site home page, folder link,
            or Copy link sharing URL. We detect the site automatically.
          </p>

          <button
            type="button"
            className="link-btn"
            onClick={() => setHelpOpen((v) => !v)}
          >
            {helpOpen ? 'Hide help' : 'Need help finding a link?'}
          </button>
          {helpOpen && (
            <div className="sp-help-panel">
              <p style={{ margin: '0 0 0.5rem' }}>Where to find it:</p>
              <ol style={{ margin: 0, paddingLeft: '1.2rem', lineHeight: 1.6 }}>
                <li>Open the SharePoint site your team uses in your browser.</li>
                <li>Copy the address bar URL, or use SharePoint &quot;Copy link&quot; on a folder.</li>
                <li>Paste below.</li>
              </ol>
              <p style={{ margin: '0.75rem 0 0.35rem', fontWeight: 600 }}>Examples we accept:</p>
              <p className="sp-mono-hint">
                https://yourcompany.sharepoint.com/sites/ProjectAlpha
                <br />
                https://yourcompany.sharepoint.com/:f:/s/ProjectAlpha/...
              </p>
              <p className="sp-mono-hint" style={{ marginTop: 8 }}>
                Do not paste personal OneDrive links (addresses with /personal/).
              </p>
            </div>
          )}

          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="sp-connect-url">Paste link</label>
            <input
              id="sp-connect-url"
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://yourcompany.sharepoint.com/sites/..."
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div
            className={`sp-preview${clientPreviewError ? ' sp-preview-error' : ''}`}
            aria-live="polite"
          >
            {clientPreviewError || (
              clientPreview ? (
                <>
                  <strong>{previewLabel}</strong>
                  <br />
                  {clientPreview}
                </>
              ) : (
                previewLabel
              )
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn"
              disabled={connecting || !clientPreview || Boolean(clientPreviewError)}
              onClick={handleConnect}
            >
              {connecting ? 'Connecting…' : 'Connect site to CODiii'}
            </button>
          </div>

          {status.text ? (
            <div className={`alert ${status.type === 'ok' ? 'alert-success' : 'alert-error'}`}>
              {status.text}
            </div>
          ) : null}
        </section>

        <section className="section">
          <h2>Connected sites</h2>
          {sitesLoading ? (
            <p className="subtitle">Loading…</p>
          ) : connectedSites.length === 0 ? (
            <p className="subtitle">No sites connected yet.</p>
          ) : (
            <div className="usage-table-wrap">
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>URL</th>
                    <th>Connected</th>
                  </tr>
                </thead>
                <tbody>
                  {connectedSites.map((site) => (
                    <tr key={site.siteId || site.siteUrl}>
                      <td>{site.siteName || '—'}</td>
                      <td>
                        {site.siteUrl ? (
                          <a href={site.siteUrl} target="_blank" rel="noopener noreferrer">
                            {site.siteUrl}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {formatDate(site.grantedAt)}
                        {site.grantedBy ? (
                          <>
                            <br />
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                              {site.grantedBy}
                            </span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="section">
          <h2>Activity</h2>
          <div className="sp-activity-log" aria-live="polite">
            {logLines.length === 0
              ? `${formatLogTime()}  Ready. Paste a SharePoint link to begin.`
              : logLines.join('\n')}
          </div>
        </section>
      </div>
    </div>
  );
}
